import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, Filter, ChevronUp, ChevronDown, Columns } from "lucide-react";

export default function DataTable({
  columns,
  fetchFn,
  onSelectionChange,
  queryHook,
  queryParams = {},
  header,
  searchValue = "",
  statusValue = "",
  roleValue = "",
  priorityValue = "",
  currentPage = 1,
  onPageChange,
  onRowClick,
  pageSize = 10, // New prop
  onPageSizeChange, // New prop
}) {
  const [page, setPage] = useState(currentPage);
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [hiddenColumns, setHiddenColumns] = useState(() => {
    // Initialize hidden columns based on the 'hidden' attribute
    const initialHidden = new Set();
    columns.forEach((col) => {
      if (col.hidden === true) {
        initialHidden.add(col.accessorKey);
      }
    });
    return initialHidden;
  });
  const [localPageSize, setLocalPageSize] = useState(pageSize);

  const queryArgs = React.useMemo(() => {
    const args = {
      page,
      limit: localPageSize,
      ...queryParams,
    };

    if (searchValue) args.search = searchValue;
    if (statusValue && statusValue !== "all") args.status = statusValue;
    if (roleValue && roleValue !== "all") args.role = roleValue;
    if (priorityValue && priorityValue !== "all") args.priority = priorityValue;

    return args;
  }, [
    page,
    localPageSize,
    searchValue,
    statusValue,
    roleValue,
    priorityValue,
    queryParams,
  ]);

  const rtqQueryResult = queryHook ? queryHook(queryArgs) : null;

  const checkboxColumn = {
    accessorKey: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          selectedRows.size === table?.data?.length && table?.data?.length > 0
        }
        onCheckedChange={(checked) => {
          if (checked) {
            const newSelected = new Set(table.data.map((row) => row._id));
            setSelectedRows(newSelected);
            onSelectionChange?.(newSelected);
          } else {
            setSelectedRows(new Set());
            onSelectionChange?.(new Set());
          }
        }}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={selectedRows.has(row._id)}
        onCheckedChange={(checked) => {
          const newSelected = new Set(selectedRows);
          if (checked) {
            newSelected.add(row._id);
          } else {
            newSelected.delete(row._id);
          }
          setSelectedRows(newSelected);
          onSelectionChange?.(newSelected);
        }}
      />
    ),
  };

  const allColumns = [checkboxColumn, ...columns];

  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(columnKey);
      setSortOrder("asc");
    }
  };

  const sortedData = React.useMemo(() => {
    if (!sortBy || !data.length) return data;

    const isDateValue = (value) => {
      if (!value) return false;
      if (value instanceof Date) return true;
      if (typeof value === "string") {
        const datePattern =
          /^\d{4}-\d{2}-\d{2}|^\d{2}\/\d{2}\/\d{4}|^\d{2}-\d{2}-\d{4}|\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
        return datePattern.test(value) && !isNaN(new Date(value).getTime());
      }
      return false;
    };

    const getColumnType = (columnKey) => {
      const column = columns.find((col) => col.accessorKey === columnKey);
      if (column?.type) return column.type;

      if (data.length > 0) {
        const sampleValue = data[0][columnKey];
        if (isDateValue(sampleValue)) return "date";
        if (typeof sampleValue === "number") return "number";
      }
      return "string";
    };

    return [...data].sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      const columnType = getColumnType(sortBy);

      switch (columnType) {
        case "date":
          aVal = new Date(aVal);
          bVal = new Date(bVal);
          break;
        case "number":
          aVal = Number(aVal) || 0;
          bVal = Number(bVal) || 0;
          break;
        case "string":
        default:
          if (typeof aVal === "string" && typeof bVal === "string") {
            aVal = aVal.toLowerCase();
            bVal = bVal.toLowerCase();
          }
          break;
      }

      if (aVal < bVal) {
        return sortOrder === "asc" ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortOrder === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortBy, sortOrder, columns]);

  const visibleColumns = allColumns.filter(
    (col) => !hiddenColumns.has(col.accessorKey)
  );

  useEffect(() => {
    if (rtqQueryResult) {
      setLoading(rtqQueryResult.isLoading || false);
      if (rtqQueryResult.data) {
        const result = rtqQueryResult.data;

        if (result.success && Array.isArray(result.data)) {
          setData(result.data || []);
          setTotalPages(result.pagination?.pages || 1);
        } else {
          setData([]);
          setTotalPages(1);
        }
      } else if (!rtqQueryResult.isLoading) {
        setData([]);
        setTotalPages(1);
      }
      return;
    }

    if (!fetchFn) return;

    const timeoutId = setTimeout(
      async () => {
        setLoading(true);
        try {
          const res = await fetchFn({
            page,
            limit: localPageSize,
            search: searchValue,
            status: statusValue,
            role: roleValue,
            priority: priorityValue,
          });

          if (res.success && Array.isArray(res.data)) {
            setData(res.data || []);
            setTotalPages(res.pagination?.pages || 1);
          } else {
            setData([]);
            setTotalPages(1);
          }
        } catch (error) {
          console.error("Error fetching data:", error);
          setData([]);
          setTotalPages(1);
        } finally {
          setLoading(false);
        }
      },
      searchValue ? 300 : 0
    );

    return () => clearTimeout(timeoutId);
  }, [
    page,
    localPageSize,
    searchValue,
    statusValue,
    roleValue,
    priorityValue,
    fetchFn,
    rtqQueryResult,
  ]);

  useEffect(() => {
    setPage(currentPage);
  }, [currentPage]);

  useEffect(() => {
    setLocalPageSize(pageSize);
  }, [pageSize]);

  const handlePageSizeChange = (newSize) => {
    const size = parseInt(newSize);
    setLocalPageSize(size);
    setPage(1); // Reset to first page
    onPageSizeChange?.(size);
    onPageChange?.(1);
  };

  return (
    <div className="space-y-4">
      {header && <div className="mb-4">{header}</div>}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {visibleColumns.map((col) => (
                <TableHead key={col.accessorKey || col.header}>
                  {col.enableSorting !== false &&
                  col.accessorKey !== "select" ? (
                    <Button
                      variant="ghost"
                      onClick={() => handleSort(col.accessorKey)}
                      className="h-auto p-0 font-normal"
                    >
                      {typeof col.header === "function"
                        ? col.header({ table: { data } })
                        : col.header}
                      {sortBy === col.accessorKey &&
                        (sortOrder === "asc" ? (
                          <ChevronUp className="ml-2 h-4 w-4" />
                        ) : (
                          <ChevronDown className="ml-2 h-4 w-4" />
                        ))}
                    </Button>
                  ) : typeof col.header === "function" ? (
                    col.header({ table: { data } })
                  ) : (
                    col.header
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={visibleColumns.length}
                  className="h-32 text-center"
                >
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-emerald-600 rounded-full animate-bounce"></div>
                      <div
                        className="w-2 h-2 bg-emerald-600 rounded-full animate-bounce"
                        style={{ animationDelay: "0.1s" }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-emerald-600 rounded-full animate-bounce"
                        style={{ animationDelay: "0.2s" }}
                      ></div>
                    </div>
                    <div className="text-sm text-gray-500">Loading...</div>
                  </div>
                </TableCell>
              </TableRow>
            ) : data?.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={visibleColumns.length}
                  className="h-24 text-center"
                >
                  <div className="flex flex-col items-center justify-center space-y-2 text-gray-500">
                    <svg
                      className="h-8 w-8"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <div className="text-sm font-medium">No Data found</div>
                    {(searchValue ||
                      statusValue ||
                      roleValue ||
                      priorityValue) && (
                      <div className="text-xs">
                        "Try adjusting your filters"
                      </div>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              sortedData?.map((row, i) => (
                <TableRow
                  key={row._id || row.id || i}
                  className={onRowClick ? "cursor-pointer" : undefined}
                  onClick={
                    onRowClick
                      ? (e) => {
                          const interactiveTags = [
                            "BUTTON",
                            "A",
                            "INPUT",
                            "LABEL",
                          ];
                          if (
                            interactiveTags.includes(e.target.tagName) ||
                            e.target.closest(
                              "button, a, input, label, [role=button], [role=checkbox]"
                            )
                          ) {
                            return;
                          }
                          onRowClick(row);
                        }
                      : undefined
                  }
                >
                  {visibleColumns.map((col) => (
                    <TableCell key={col.accessorKey}>
                      {typeof col.cell === "function"
                        ? col.cell({ row })
                        : row[col.accessorKey]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center gap-4">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                <Columns className="mr-2 h-4 w-4" />
                Columns
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-48">
              <div className="space-y-2">
                <div className="font-medium text-sm">Toggle columns</div>
                {allColumns.slice(1).map((col) => (
                  <div
                    key={col.accessorKey}
                    className="flex items-center space-x-2"
                  >
                    <Checkbox
                      checked={!hiddenColumns.has(col.accessorKey)}
                      onCheckedChange={(checked) => {
                        const newHidden = new Set(hiddenColumns);
                        if (checked) {
                          newHidden.delete(col.accessorKey);
                        } else {
                          newHidden.add(col.accessorKey);
                        }
                        setHiddenColumns(newHidden);
                      }}
                    />
                    <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      {typeof col.header === "string"
                        ? col.header
                        : col.accessorKey}
                    </label>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Rows per page:</span>
            <Select
              value={localPageSize.toString()}
              onValueChange={handlePageSizeChange}
            >
              <SelectTrigger className="w-[70px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          {totalPages > 1 && (
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => {
                      const newPage = Math.max(1, page - 1);
                      setPage(newPage);
                      onPageChange?.(newPage);
                    }}
                    className={
                      page <= 1
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>

                {page > 2 && (
                  <PaginationItem>
                    <PaginationLink
                      onClick={() => {
                        setPage(1);
                        onPageChange?.(1);
                      }}
                      className="cursor-pointer"
                    >
                      1
                    </PaginationLink>
                  </PaginationItem>
                )}

                {page > 3 && (
                  <PaginationItem>
                    <PaginationEllipsis />
                  </PaginationItem>
                )}

                {page > 1 && (
                  <PaginationItem>
                    <PaginationLink
                      onClick={() => {
                        const newPage = page - 1;
                        setPage(newPage);
                        onPageChange?.(newPage);
                      }}
                      className="cursor-pointer"
                    >
                      {page - 1}
                    </PaginationLink>
                  </PaginationItem>
                )}

                <PaginationItem>
                  <PaginationLink isActive>{page}</PaginationLink>
                </PaginationItem>

                {page < totalPages && (
                  <PaginationItem>
                    <PaginationLink
                      onClick={() => {
                        const newPage = page + 1;
                        setPage(newPage);
                        onPageChange?.(newPage);
                      }}
                      className="cursor-pointer"
                    >
                      {page + 1}
                    </PaginationLink>
                  </PaginationItem>
                )}

                {page < totalPages - 2 && (
                  <PaginationItem>
                    <PaginationEllipsis />
                  </PaginationItem>
                )}

                {page < totalPages - 1 && (
                  <PaginationItem>
                    <PaginationLink
                      onClick={() => {
                        setPage(totalPages);
                        onPageChange?.(totalPages);
                      }}
                      className="cursor-pointer"
                    >
                      {totalPages}
                    </PaginationLink>
                  </PaginationItem>
                )}

                <PaginationItem>
                  <PaginationNext
                    onClick={() => {
                      const newPage = Math.min(totalPages, page + 1);
                      setPage(newPage);
                      onPageChange?.(newPage);
                    }}
                    className={
                      page >= totalPages
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </div>
      </div>
    </div>
  );
}
